/**
 * LiveTicker
 *
 * Compact horizontal KPI bar that sits below the header.
 * Displays static GA4 metrics for Monarch Casino with simulated
 * refresh behaviour (no real API — values jitter +/- 1-3% on manual refresh).
 *
 * Values flash green/red briefly when they change.
 * Includes a "Last updated X seconds ago" counter and refresh button.
 *
 * Style: bg-bg-1 border-b border-border, solid colors, no gradients.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { formatNumber, formatPercentRaw } from '@/lib/formatting';

// ---------------------------------------------------------------------------
// Static GA4 base data
// ---------------------------------------------------------------------------

interface BaseMetric {
  label: string;
  value: number;
  format: 'number' | 'percent';
}

const BASE_METRICS: BaseMetric[] = [
  { label: 'Sessions', value: 87615, format: 'number' },
  { label: 'Engagement Rate', value: 79.49, format: 'percent' },
  { label: 'Rsv. Widget Clicks', value: 13000, format: 'number' },
  { label: 'Form Submissions', value: 118, format: 'number' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TickerMetric {
  label: string;
  displayValue: string;
  rawValue: number;
}

type FlashState = 'none' | 'up' | 'down';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return a value jittered by +/- 1-3% */
function jitter(base: number): number {
  const pct = (1 + (Math.random() * 2)) / 100; // 1-3 %
  const direction = Math.random() > 0.5 ? 1 : -1;
  return base * (1 + direction * pct);
}

function formatMetric(value: number, format: 'number' | 'percent'): string {
  if (format === 'percent') return formatPercentRaw(value, 1);
  return formatNumber(Math.round(value));
}

function deriveMetrics(values: number[]): TickerMetric[] {
  return BASE_METRICS.map((m, i) => ({
    label: m.label,
    displayValue: formatMetric(values[i], m.format),
    rawValue: values[i],
  }));
}

// ---------------------------------------------------------------------------
// Metric Chip -- single KPI with flash animation
// ---------------------------------------------------------------------------

function MetricChip({
  label,
  value,
  flash,
}: {
  label: string;
  value: string;
  flash: FlashState;
}) {
  return (
    <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-3 py-1">
      <span className="text-2xs sm:text-xs text-text-3 uppercase tracking-wider font-medium whitespace-nowrap">
        {label}
      </span>
      <span
        className={`
          text-sm font-mono font-bold text-text whitespace-nowrap
          transition-colors duration-700
          ${flash === 'up' ? 'text-success' : ''}
          ${flash === 'down' ? 'text-danger' : ''}
        `}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LiveTicker Component
// ---------------------------------------------------------------------------

export default function LiveTicker() {
  // Current raw values — start from base
  const [currentValues, setCurrentValues] = useState<number[]>(
    () => BASE_METRICS.map((m) => m.value),
  );
  const [refreshing, setRefreshing] = useState(false);

  // Flash states keyed by label
  const prevValuesRef = useRef<Record<string, number>>({});
  const [flashStates, setFlashStates] = useState<Record<string, FlashState>>({});

  // "Last updated" counter
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);

  // Tick the "seconds ago" counter every second
  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  // Derive display metrics from current values
  const metrics = deriveMetrics(currentValues);

  // Detect value changes and trigger flash
  useEffect(() => {
    const newFlash: Record<string, FlashState> = {};
    const prev = prevValuesRef.current;

    for (const m of metrics) {
      const prevVal = prev[m.label];
      if (prevVal !== undefined && prevVal !== m.rawValue) {
        newFlash[m.label] = m.rawValue > prevVal ? 'up' : 'down';
      } else {
        newFlash[m.label] = 'none';
      }
      prev[m.label] = m.rawValue;
    }

    setFlashStates(newFlash);

    // Clear flash after 700ms
    const timeout = setTimeout(() => {
      setFlashStates({});
    }, 700);
    return () => clearTimeout(timeout);
  }, [currentValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual refresh: jitter values to simulate data update
  const handleManualRefresh = useCallback(() => {
    setRefreshing(true);

    // Short delay to show spinner
    setTimeout(() => {
      setCurrentValues(BASE_METRICS.map((m) => jitter(m.value)));
      setLastUpdated(Date.now());
      setSecondsAgo(0);
      setRefreshing(false);
    }, 400);
  }, []);

  return (
    <div className="bg-bg-1 border-b border-border px-2 sm:px-4 py-1 sm:py-1.5 flex items-center gap-0.5 sm:gap-1 overflow-x-auto no-scrollbar">
      {/* Metric chips */}
      {metrics.map((m, i) => (
        <span key={m.label} className="flex items-center">
          <MetricChip
            label={m.label}
            value={m.displayValue}
            flash={flashStates[m.label] || 'none'}
          />
          {i < metrics.length - 1 && (
            <span className="w-px h-4 bg-border mx-0.5 sm:mx-1 shrink-0" />
          )}
        </span>
      ))}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Last updated + refresh */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-text-3 tabular-nums whitespace-nowrap hidden sm:inline">
          Updated {secondsAgo}s ago
        </span>

        {/* Spinning dot indicator (Monarch gold) */}
        {refreshing && (
          <span className="w-2 h-2 rounded-full bg-monarch-gold animate-spin-slow shrink-0" />
        )}

        {/* Manual refresh button */}
        <button
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="p-1 rounded-sm text-text-3 hover:text-text hover:bg-bg-2 transition-colors disabled:opacity-40 cursor-pointer"
          title="Refresh now"
        >
          <svg
            className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
